import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useFotosCatalogo } from './useFotosCatalogo';

/**
 * Panel lateral («peek», patrón Notion/Linear) del listado de productos: al
 * pulsar una fila se ve el resumen del producto sin salir de la lista; doble
 * clic o «Abrir ficha» navegan. Usa GET /productos/:id/resumen.
 */

type Resumen = {
  producto: { id: string; sku: string; nombre: string; estado: string; cliente_codigo: string | null };
  cabecera: {
    coste_entrada: { valor: number | null; fecha: string; uds: number } | null;
    coste_teorico: number | null; pvpr: number | null;
    precio_cliente: { valor: number; fecha: string; fuente: string } | null;
    margen_pct: number | null;
    stock: { actual: number | null; fecha: string | null; cobertura: number | null; unidad: 'meses' | 'semanas' | null; venta_media: number | null; umbral: number | null };
    proxima_entrada: { uds: number; eta: string | null; numero: string | null } | null;
  };
  contadores: { pedidos: number; pedidos_abiertos: number; cotizaciones: number; ventas_approx: number; archivos: number };
  alertas: { tono: 'good' | 'warn' | 'bad' | 'neutral'; titulo: string; detalle: string }[];
};

export function ProductoPeek({ producto, clienteCodigo, onClose }: { producto: { id: string; sku: string; nombre: string; estado_nombre?: string | null; estado_color?: string | null; marca_nombre?: string | null; gama_nombre?: string | null; subcategoria_nombre?: string | null }; clienteCodigo: string; onClose: () => void }) {
  const base = `/${clienteCodigo.toLowerCase()}`;
  const q = useQuery({ queryKey: ['producto', producto.id, 'resumen'], queryFn: () => api.get<Resumen>(`/productos/${producto.id}/resumen`) });
  const c = q.data?.cabecera;
  const n = q.data?.contadores;
  const cobBad = c?.stock.cobertura != null && c.stock.umbral != null && c.stock.cobertura < c.stock.umbral;
  // La misma consulta (y caché) que la vista catálogo: foto principal o, si no hay, la del proveedor.
  const fotos = useFotosCatalogo(clienteCodigo);
  const foto = fotos.data?.urls[producto.id];
  const fotoProveedor = fotos.data?.proveedor.has(producto.id) ?? false;
  return (
    <aside aria-label="Vista rápida del producto" className="flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start gap-2 border-b border-slate-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold leading-snug text-slate-900">{producto.nombre}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span className="font-mono">{producto.sku}</span>
            {producto.estado_nombre && <Badge color={producto.estado_color} dot>{producto.estado_nombre}</Badge>}
            {producto.marca_nombre && <Badge tone="neutral">{producto.marca_nombre}</Badge>}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar vista rápida" className="cursor-pointer rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm">
        <div className="relative mb-3 flex h-44 items-center justify-center rounded-md bg-slate-50">
          {foto
            ? <img src={foto} alt={producto.nombre} className="max-h-full max-w-full object-contain p-2" />
            : <span className="text-xs text-slate-400">{fotos.isLoading ? 'Cargando foto…' : 'Sin foto'}</span>}
          {foto && fotoProveedor && <span className="absolute left-2 top-2 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Foto proveedor</span>}
        </div>
        {q.isLoading && <p className="text-xs text-slate-400">Cargando…</p>}
        {q.error && <p role="alert" className="text-xs text-bad-600">No se pudo cargar el resumen.</p>}
        {c && (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-slate-500">Stock · cobertura</dt><dd className="text-right font-semibold">{c.stock.actual != null ? fmt.int(c.stock.actual) : '—'}{c.stock.cobertura != null && <span className={cobBad ? 'text-warn-600' : 'text-slate-500'}> · {fmt.number(c.stock.cobertura)} {c.stock.unidad === 'meses' ? 'meses' : 'sem.'}</span>}</dd>
              <dt className="text-slate-500">Venta media</dt><dd className="text-right font-semibold">{c.stock.venta_media != null ? `${fmt.number(c.stock.venta_media)} uds/${c.stock.unidad === 'meses' ? 'mes' : 'sem.'}` : '—'}</dd>
              <dt className="text-slate-500">Coste de entrada</dt><dd className="text-right font-semibold">{c.coste_entrada?.valor != null ? fmt.eur(c.coste_entrada.valor) : c.coste_teorico != null ? <span title="Coste teórico (sin recepciones)">{fmt.eur(c.coste_teorico)} <span className="font-normal text-slate-400">teórico</span></span> : '—'}</dd>
              <dt className="text-slate-500">Precio a {clienteCodigo}</dt><dd className="text-right font-semibold">{c.precio_cliente ? <>{fmt.eur(c.precio_cliente.valor)} <span className="font-normal text-slate-400">{fmt.date(c.precio_cliente.fecha)}</span></> : '—'}</dd>
              <dt className="text-slate-500">Margen APPROX</dt><dd className="text-right font-semibold">{c.margen_pct != null ? fmt.pct(c.margen_pct) : '—'}</dd>
              <dt className="text-slate-500">PVPr</dt><dd className="text-right font-semibold">{c.pvpr != null ? fmt.eur(c.pvpr) : '—'}</dd>
              <dt className="text-slate-500">Próxima entrada</dt><dd className="text-right font-semibold">{c.proxima_entrada ? <>{fmt.int(c.proxima_entrada.uds)} uds{c.proxima_entrada.eta && <span className="font-normal text-slate-400"> · ETA {fmt.date(c.proxima_entrada.eta)}</span>}</> : '—'}</dd>
            </dl>
            {n && (
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-slate-600">
                <span className="rounded border border-slate-200 px-2 py-0.5"><b>{n.pedidos}</b> pedidos{n.pedidos_abiertos ? ` · ${n.pedidos_abiertos} abiertos` : ''}</span>
                <span className="rounded border border-slate-200 px-2 py-0.5"><b>{n.cotizaciones}</b> cotizaciones</span>
                <span className="rounded border border-slate-200 px-2 py-0.5"><b>{n.ventas_approx}</b> ventas APPROX</span>
                <span className="rounded border border-slate-200 px-2 py-0.5"><b>{n.archivos}</b> docs</span>
              </div>
            )}
            {q.data && q.data.alertas.length > 0 && (
              <div className="mt-3 space-y-1">
                {q.data.alertas.map((a, i) => <div key={i} className="flex items-start gap-2 text-xs"><Badge tone={a.tono} dot>{a.titulo}</Badge><span className="text-slate-700">{a.detalle}</span></div>)}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2 border-t border-slate-200 px-4 py-3">
        <Link to={`${base}/productos/${producto.id}`}><Button size="sm" variant="primary">Abrir ficha</Button></Link>
        <span className="ml-auto self-center text-[11px] text-slate-400">Doble clic en la fila abre la ficha</span>
      </div>
    </aside>
  );
}
